// ===========================================
// Cookie Consent Context - Provider Component
// ===========================================
// Manages GDPR + Australian Privacy Act compliant cookie consent
// Stores consent preferences in localStorage

import { useState, useCallback, useEffect, useContext, ReactNode, createContext } from 'react';

export interface CookieConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  hasConsented: boolean;
  consentDate?: string;
  version: string;
}

export interface CookieConsentContextValue {
  state: CookieConsentState;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (category: keyof Omit<CookieConsentState, 'hasConsented' | 'consentDate' | 'version'>, value: boolean) => void;
  saveConsent: () => void;
  resetConsent: () => void;
}

const STORAGE_KEY = 'mnm_cookie_consent';
const CURRENT_VERSION = '1.0';

const defaultState: CookieConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
  hasConsented: false,
  version: CURRENT_VERSION,
};

export const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [state, setState] = useState<CookieConsentState>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as CookieConsentState;
        // If version matches and hasConsented is true, use saved state
        if (parsed.version === CURRENT_VERSION && parsed.hasConsented) {
          return parsed;
        }
      }
    } catch {
      // Ignore errors (SSR, private browsing, etc.)
    }
    // Return default state
    return defaultState;
  });

  // Persist to localStorage when state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore errors
    }
  }, [state]);

  const acceptAll = useCallback(() => {
    setState({
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
      hasConsented: true,
      consentDate: new Date().toISOString(),
      version: CURRENT_VERSION,
    });
  }, []);

  const rejectNonEssential = useCallback(() => {
    setState({
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
      hasConsented: true,
      consentDate: new Date().toISOString(),
      version: CURRENT_VERSION,
    });
  }, []);

  const updateConsent = useCallback((category: keyof Omit<CookieConsentState, 'hasConsented' | 'consentDate' | 'version'>, value: boolean) => {
    setState((prev) => {
      // Essential cannot be toggled off
      if (category === 'essential' && !value) {
        return prev;
      }
      return {
        ...prev,
        [category]: value,
      };
    });
  }, []);

  const saveConsent = useCallback(() => {
    setState((prev) => ({
      ...prev,
      hasConsented: true,
      consentDate: new Date().toISOString(),
      version: CURRENT_VERSION,
    }));
  }, []);

  const resetConsent = useCallback(() => {
    setState(defaultState);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore errors
    }
  }, []);

  const value: CookieConsentContextValue = {
    state,
    acceptAll,
    rejectNonEssential,
    updateConsent,
    saveConsent,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

// Hook to use the cookie consent context
export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
